import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SCHENGEN_COUNTRIES, exampleFileUrl } from "@/lib/countries";
import { Trash2, Plus, Upload, Save, RotateCcw, Star, X, FileText } from "lucide-react";
import { PreviewCard } from "@/components/DocumentMockups";
import ScrollRow from "@/components/ScrollRow";
import { CountryCombobox } from "@/components/CountryCombobox";
import { Flag } from "@/components/Flag";

function newItem() { return { title: "", package_contents: "", description: "", example_file_url: null }; }

function newPackage() {
  return {
    id: `pkg_${Math.random().toString(36).slice(2, 9)}`,
    name: "New Package",
    price_normal: 0,
    price_express: 0,
    delivery_normal: "24 hours",
    delivery_express: "3 hours",
    currency: "usd",
    most_popular: false,
    items: [newItem()],
  };
}

export default function AdminPackages() {
  const [code, setCode] = useState("");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (c) => {
    if (!c) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/packages/${c}`);
      setPackages(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (code) load(code); }, [code, load]);

  const updatePkg = (i, patch) => setPackages((arr) => arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const updateItem = (pi, ii, patch) => setPackages((arr) => arr.map((p, idx) => idx !== pi ? p : { ...p, items: p.items.map((it, j) => j === ii ? { ...it, ...patch } : it) }));
  const addPackage = () => setPackages((arr) => [...arr, newPackage()]);
  const removePackage = (i) => { if (confirm("Remove this package?")) setPackages((arr) => arr.filter((_, idx) => idx !== i)); };
  const addItem = (pi) => setPackages((arr) => arr.map((p, idx) => idx !== pi ? p : { ...p, items: [...p.items, newItem()] }));
  const removeItem = (pi, ii) => setPackages((arr) => arr.map((p, idx) => idx !== pi ? p : { ...p, items: p.items.filter((_, j) => j !== ii) }));

  const uploadExample = async (pi, ii, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/packages/${code}/upload-example`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateItem(pi, ii, { example_file_url: data.example_file_url });
      toast.success("Example uploaded");
    } catch {
      toast.error("Upload failed (only JPG, PNG, PDF)");
    }
  };

  const save = async () => {
    if (!code) return;
    setSaving(true);
    try {
      await api.put(`/admin/packages/${code}`, { packages });
      toast.success("Saved");
      load(code);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!code) return;
    if (!confirm(`Reset ${code} back to the default template?`)) return;
    try {
      await api.delete(`/admin/packages/${code}`);
      toast.success("Reset to default");
      load(code);
    } catch {
      toast.error("Reset failed");
    }
  };

  const country = SCHENGEN_COUNTRIES.find((c) => c.code === code);

  return (
    <div data-testid="admin-packages-page">
      <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] font-semibold">Packages</div>
      <h1 className="mt-1 font-display font-bold text-3xl text-[#0f1b2d]">Per-country packages</h1>
      <p className="mt-1 text-[#475569]">Customize packages, pricing and document examples for each Schengen country.</p>

      {/* Country picker */}
      <div className="mt-6 max-w-md">
        <label className="text-sm font-semibold text-[#0f1b2d]">Select country</label>
        <div className="mt-1.5">
          <CountryCombobox
            options={SCHENGEN_COUNTRIES}
            value={code}
            onChange={(v) => setCode(v)}
            valueField="code"
            placeholder="— Choose a country —"
            testId="admin-packages-country-select"
          />
        </div>
      </div>

      {code && (
        <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* EDITOR */}
          <div className="xl:col-span-2 space-y-5">
            {loading ? (
              <div className="text-[#475569]">Loading…</div>
            ) : (
              <>
                {packages.map((p, pi) => (
                  <div key={p.id} data-testid={`admin-pkg-${pi}`} className="bg-white border border-[#e2e8f0] rounded-lg p-5 space-y-4">
                    <div className="flex items-start gap-3 flex-wrap">
                      <input
                        value={p.name}
                        onChange={(e) => updatePkg(pi, { name: e.target.value })}
                        data-testid={`admin-pkg-name-${pi}`}
                        placeholder="Package name"
                        className="flex-1 min-w-[180px] font-display font-bold text-lg text-[#0f1b2d] border-b-2 border-[#e2e8f0] focus:border-[#2563eb] outline-none px-1 py-1"
                      />
                      <button
                        onClick={() => updatePkg(pi, { most_popular: !p.most_popular })}
                        data-testid={`admin-pkg-popular-${pi}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border ${p.most_popular ? "bg-[#2563eb] text-white border-[#2563eb]" : "bg-white text-[#475569] border-[#e2e8f0]"}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${p.most_popular ? "fill-current" : ""}`} /> Most popular
                      </button>
                      <button
                        onClick={() => removePackage(pi)}
                        data-testid={`admin-pkg-delete-${pi}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete package
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-[#0f1b2d]">Normal price ($)</label>
                        <input
                          type="number" min={0}
                          value={p.price_normal}
                          onChange={(e) => updatePkg(pi, { price_normal: parseFloat(e.target.value || "0") })}
                          data-testid={`admin-pkg-price-normal-${pi}`}
                          className="mt-1 w-full border border-[#e2e8f0] rounded-md px-3 py-2 text-sm outline-none focus:border-[#2563eb]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-[#0f1b2d]">Express price ($)</label>
                        <input
                          type="number" min={0}
                          value={p.price_express}
                          onChange={(e) => updatePkg(pi, { price_express: parseFloat(e.target.value || "0") })}
                          data-testid={`admin-pkg-price-express-${pi}`}
                          className="mt-1 w-full border border-[#e2e8f0] rounded-md px-3 py-2 text-sm outline-none focus:border-[#2563eb]"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#64748b] mb-2">Items</div>
                      <div className="space-y-3">
                        {p.items.map((it, ii) => (
                          <PackageItemEditor
                            key={ii}
                            item={it}
                            onChange={(patch) => updateItem(pi, ii, patch)}
                            onRemove={() => removeItem(pi, ii)}
                            onUpload={(file) => uploadExample(pi, ii, file)}
                            testIdPrefix={`admin-pkg-${pi}-item-${ii}`}
                          />
                        ))}
                        <button
                          onClick={() => addItem(pi)}
                          data-testid={`admin-pkg-add-item-${pi}`}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563eb] hover:text-[#1d4ed8]"
                        >
                          <Plus className="w-4 h-4" /> Add item
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={addPackage}
                  data-testid="admin-add-package"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#e2e8f0] hover:border-[#2563eb] rounded-md text-sm font-semibold text-[#475569] hover:text-[#2563eb]"
                >
                  <Plus className="w-4 h-4" /> Add package
                </button>

                <div className="flex items-center gap-3 sticky bottom-4 bg-[#f8fafc]/95 backdrop-blur p-3 rounded-lg border border-[#e2e8f0]">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid="admin-pkg-save"
                    className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-md"
                  >
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : `Save changes for ${code}`}
                  </button>
                  <button
                    onClick={resetDefault}
                    data-testid="admin-pkg-reset"
                    className="inline-flex items-center gap-2 border border-[#e2e8f0] hover:border-[#94a3b8] text-[#0f1b2d] font-semibold px-5 py-2.5 rounded-md"
                  >
                    <RotateCcw className="w-4 h-4" /> Reset to default
                  </button>
                </div>
              </>
            )}
          </div>

          {/* LIVE PREVIEW */}
          <div className="xl:col-span-1">
            <div className="sticky top-24">
              <div className="text-xs uppercase tracking-[0.2em] text-[#64748b] font-semibold mb-3 inline-flex items-center gap-1.5">
                <span>Live preview ·</span>
                {country && <Flag code={country.code} />}
                <span>{country?.name}</span>
              </div>
              <div className="bg-[#0f1b2d] p-4 rounded-xl">
                <HeroApplicationCardPreview packages={packages} />
              </div>
              <p className="mt-2 text-[11px] text-[#64748b] italic">Preview updates as you edit. Save to publish.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PackageItemEditor({ item, onChange, onRemove, onUpload, testIdPrefix }) {
  const fileRef = useRef(null);
  return (
    <div className="p-3 rounded-md border border-[#e2e8f0] bg-[#f8fafc] flex items-start gap-3" data-testid={testIdPrefix}>
      <div className="flex-1 space-y-2">
        <input
          value={item.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Item title (e.g. Flight Reservation)"
          data-testid={`${testIdPrefix}-title`}
          className="w-full text-sm font-semibold text-[#0f1b2d] bg-transparent border-b border-[#e2e8f0] focus:border-[#2563eb] outline-none px-1 py-1"
        />
        <input
          value={item.package_contents || ""}
          onChange={(e) => onChange({ package_contents: e.target.value })}
          placeholder="Package contents (e.g. Confirmed Airline Ticket)"
          data-testid={`${testIdPrefix}-contents`}
          className="w-full text-xs font-medium text-[#0f1b2d] bg-transparent border-b border-[#e2e8f0] focus:border-[#2563eb] outline-none px-1 py-1"
        />
        <input
          value={item.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Optional description"
          data-testid={`${testIdPrefix}-desc`}
          className="w-full text-xs text-[#475569] bg-transparent border-b border-[#e2e8f0] focus:border-[#2563eb] outline-none px-1 py-1"
        />
      </div>
      <div className="flex items-center gap-2">
        {item.example_file_url ? (
          <div className="relative group" data-testid={`${testIdPrefix}-preview`}>
            <a href={exampleFileUrl(item.example_file_url)} target="_blank" rel="noreferrer" className="block w-14 h-14 rounded border border-[#e2e8f0] overflow-hidden bg-white grid place-items-center">
              {/\.pdf$/i.test(item.example_file_url) ? (
                <FileText className="w-5 h-5 text-[#475569]" />
              ) : (
                <img src={exampleFileUrl(item.example_file_url)} alt={`${item.title || "Document"} sample`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              )}
            </a>
            <button
              onClick={() => onChange({ example_file_url: null })}
              data-testid={`${testIdPrefix}-remove-file`}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] grid place-items-center"
              title="Remove example"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            data-testid={`${testIdPrefix}-upload`}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-[#e2e8f0] text-[#475569] hover:border-[#2563eb] hover:text-[#2563eb]"
          >
            <Upload className="w-3.5 h-3.5" /> Upload example
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          hidden
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <button onClick={onRemove} className="p-1.5 rounded text-red-600 hover:bg-red-50" data-testid={`${testIdPrefix}-delete`} title="Delete item">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// Preview wrapper that passes the in-memory edits to the same hero card,
// but disables fetching by passing defaultPackages and leaving country empty -
// instead we render a manual mini-preview that mirrors the hero card UI directly.
function HeroApplicationCardPreview({ packages }) {
  // Use the hero card's own preview mode by short-circuiting via memo prop.
  return <HeroApplicationCardStatic packages={packages} />;
}

// Lightweight static preview that mirrors HeroApplicationCard look without fetching.
function HeroApplicationCardStatic({ packages }) {
  const [delivery, setDelivery] = useState("normal");
  const [selectedPkg, setSelectedPkg] = useState(packages[0]?.id || "");
  useEffect(() => {
    if (!packages.find((p) => p.id === selectedPkg)) setSelectedPkg(packages[0]?.id || "");
  }, [packages, selectedPkg]);
  const priceFor = (p) => (delivery === "express" ? p.price_express : p.price_normal);
  return (
    <div className="w-full bg-white text-[#0f1b2d] rounded-xl shadow-2xl border border-white/10 p-5">
      <div className="text-xs uppercase tracking-widest text-[#64748b]">Get started</div>
      <h3 className="font-display font-bold text-lg">Start your application</h3>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={() => setDelivery("normal")} className={`text-xs font-semibold py-2 rounded-md border-2 ${delivery === "normal" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569]"}`}>24h</button>
        <button onClick={() => setDelivery("express")} className={`text-xs font-semibold py-2 rounded-md border-2 ${delivery === "express" ? "border-[#2563eb] bg-[#eff6ff] text-[#2563eb]" : "border-[#e2e8f0] text-[#475569]"}`}>Express 3h</button>
      </div>
      <div className="mt-4 space-y-2.5">
        {packages.map((p) => {
          const active = selectedPkg === p.id;
          return (
            <button key={p.id} type="button" onClick={() => setSelectedPkg(p.id)} className={`w-full text-left p-3 rounded-md border-2 relative ${active ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0]"}`}>
              {p.most_popular && <div className="absolute -top-2 right-3 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-[#2563eb] text-white rounded">Most popular</div>}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-[#0f1b2d]">{p.name || "—"}</div>
                  <div className="text-[11px] text-[#64748b]">{(p.items || []).length} items</div>
                </div>
                <div className="font-display font-black text-lg text-[#0f1b2d]">${Math.round(priceFor(p) || 0)}</div>
              </div>
              {active && (p.items || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#bfdbfe]">
                  <div className="text-[10px] uppercase tracking-wider text-[#64748b] mb-1.5">Preview</div>
                  <ScrollRow ariaLabel="Live preview row">
                    {(p.items || []).map((it, i) => (
                      <PreviewCard key={i} item={it} size="thumb" />
                    ))}
                  </ScrollRow>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
